import { AppConstants } from '@core-lib/config';
import { IDatagridOptions, IDatagridSettings, IDatagridSort, IList } from '@core-lib/models';
import { MemoizedSelector, ReducerTypes, createSelector, on, props } from '@ngrx/store';
import { createTypedAction } from './action-creators.function';
import { CorrelationParams, newAggregationId } from './correlated-actions.model';

/**
 * ========================================================================
 * Datagrid slice creation helpers
 * Source: https://github.com/vincent/ng-stator
 * ========================================================================
 */

/**
 * State Model
 */
export interface IStoreDatagridStateSlice<List extends IList<any>, SortableCols> {
	list: List;
	settings: IDatagridSettings<SortableCols>;
	error: any;
	isLoading: boolean;
}

export function createDataGridSlice<List extends IList<any>, SortableCols>(
	defaultSort?: IDatagridSort<SortableCols>,
	defaultOptions: Partial<IDatagridOptions> = {}
): IStoreDatagridStateSlice<List, SortableCols> {
	const options = {
		...AppConstants.DATAGRID_OPTIONS_DEFAULTS,
		...defaultOptions
	};

	return {
		list: <List>{
			items: []
		},
		settings: {
			sort: defaultSort,
			pageNumber: 1,
			pageSize: options.orginalPageSize,
			options
		},
		error: null,
		isLoading: true
	};
}

//pour le tableau validation opération
export function createDataGridSliceForValidationTable<List extends IList<any>, SortableCols>(
	defaultSort?: IDatagridSort<SortableCols>,
	defaultOptions: Partial<IDatagridOptions> = {}
): IStoreDatagridStateSlice<List, SortableCols> {
	const options = {
		...AppConstants.DATAGRID_VALIDATION_OPTIONS,
		...defaultOptions
	};

	return {
		list: <List>{
			items: []
		},
		settings: {
			sort: defaultSort,
			pageNumber: 1,
			pageSize: options.orginalPageSize,
			options
		},
		error: null,
		isLoading: true
	};
}

/**
 * Datagrid Action Types
 */
export enum DatagridActionType {
	LoadDatagridByIdRequest = 'LoadDatagridByIdRequest',
	LoadDatagridRequest = 'LoadDatagridRequest',
	LoadDatagridSuccess = 'LoadDatagridSuccess',
	LoadDatagridFailure = 'LoadDatagridFailure',
	UpdateDatagridSettings = 'UpdateDatagridSettings'
}

/**
 * Action Creators
 */
export function createDatagridActions<List, Props>(storeName: string, datagridName: string) {
	return {
		LoadDatagridByIdRequest: createTypedAction(
			DatagridActionType.LoadDatagridRequest,
			`[${storeName}] Load ${datagridName} Request`,
			(
				id: string | number,
				correlationParams: CorrelationParams = {
					correlationId: newAggregationId()
				}
			) => ({ id, correlationParams })
		),

		LoadDatagridRequest: createTypedAction(
			DatagridActionType.LoadDatagridRequest,
			`[${storeName}] Load ${datagridName} Request`,
			(
				correlationParams: CorrelationParams = {
					correlationId: newAggregationId()
				}
			) => ({ correlationParams })
		),

		LoadDatagridSuccess: createTypedAction(
			DatagridActionType.LoadDatagridSuccess,
			`[${storeName}] Load ${datagridName} Success`,
			props<{ list: List; correlationParams?: CorrelationParams }>()
		),

		LoadDatagridFailure: createTypedAction(
			DatagridActionType.LoadDatagridFailure,
			`[${storeName}] Load ${datagridName} Failure`,
			props<{ error: Error }>()
		),

		UpdateDatagridSettings: createTypedAction(
			DatagridActionType.UpdateDatagridSettings,
			`[${storeName}] Update ${datagridName} Settings`,
			props<{ settings: IDatagridSettings<Props> }>()
		)
	};
}

/**
 * Reducers
 */
export function datagridSliceRequestReducer<S extends object>(state: S, slice: keyof S) {
	return {
		...state,
		[slice]: {
			...state[slice],
			isLoading: true,
			error: false
		}
	};
}

export function datagridSliceFailureReducer<S extends object, A extends { error: any }>(state: S, slice: keyof S, action: A) {
	return {
		...state,
		[slice]: {
			...state[slice],
			isLoading: false,
			error: action.error
		}
	};
}

export function datagridSliceSuccessReducer<S extends object, A extends { list: IList<any> }>(state: S, slice: keyof S, action: A) {
	const theSlice = <IStoreDatagridStateSlice<any, any>>(<unknown>state[slice]);
	// We refresh the total count only if we are in the first datagrid page
	let newlist = theSlice.settings?.pageNumber == 1
					? action.list 
					: {
						...theSlice.list,
						items: action.list.items,
						pageNumber: action.list.pageNumber,
						pageSize: action.list.pageSize,
					};
	return {
		...state,
		[slice]: {
			...theSlice,
			list: newlist,
			settings: {
				...theSlice.settings,
				pageNumber: action.list.pageNumber,
				pageSize: action.list.pageSize
			},
			isLoading: false,
			error: null
		}
	};
}

export function datagridSliceUpdateSettingsReducer<S extends object, A extends { settings: IDatagridSettings<any> }>(
	state: S,
	slice: keyof S,
	action: A
) {
	const theSlice = <IStoreDatagridStateSlice<any, any>>(<unknown>state[slice]);
	return {
		...state,
		[slice]: {
			...theSlice,
			settings: {
				...theSlice.settings,
				...action.settings
			}
		}
	};
}

export function withDatagridReducer<S extends object>(
	slice: keyof S,
	{ LoadDatagridByIdRequest, LoadDatagridRequest, LoadDatagridSuccess, LoadDatagridFailure, UpdateDatagridSettings }
): ReducerTypes<any, any>[] {
	return [
		on(LoadDatagridByIdRequest, (state: S, action) => datagridSliceRequestReducer(state, slice)),
		on(LoadDatagridRequest, (state: S, action) => datagridSliceRequestReducer(state, slice)),
		on(LoadDatagridSuccess, <A extends { list: IList<any> }>(state: S, action: A) => datagridSliceSuccessReducer(state, slice, action)),
		on(LoadDatagridFailure, <A extends { error: any }>(state: S, action: A) => datagridSliceFailureReducer(state, slice, action)),
		on(UpdateDatagridSettings, <A extends { settings: IDatagridSettings<any> }>(state: S, action: A) =>
			datagridSliceUpdateSettingsReducer(state, slice, action)
		)
	];
}

/**
 * Selectors
 */
export interface IStoreListStateSelectors<List extends IList<any>, SortableCols> {
	getList: (state: IStoreDatagridStateSlice<List, SortableCols>) => List;
	selectList: MemoizedSelector<object, List>;

	getSettings: (state: IStoreDatagridStateSlice<List, SortableCols>) => IDatagridSettings<SortableCols>;
	selectSettings: MemoizedSelector<object, IDatagridSettings<SortableCols>>;

	getError: (state: IStoreDatagridStateSlice<List, SortableCols>) => any;
	selectError: MemoizedSelector<object, any>;

	getIsLoading: (state: IStoreDatagridStateSlice<List, SortableCols>) => boolean;
	selectIsLoading: MemoizedSelector<object, boolean>;
}

export function createDatagridSliceSelectors<List extends IList<any>, SortableCols>(
	sliceSelector: MemoizedSelector<object, IStoreDatagridStateSlice<List, SortableCols>>
): IStoreListStateSelectors<List, SortableCols> {
	const getList = (state: IStoreDatagridStateSlice<List, SortableCols>): List => state.list;
	const selectList: MemoizedSelector<object, List> = createSelector(sliceSelector, getList);

	const getSettings = (state: IStoreDatagridStateSlice<List, SortableCols>): IDatagridSettings<SortableCols> => state.settings;
	const selectSettings: MemoizedSelector<object, IDatagridSettings<SortableCols>> = createSelector(sliceSelector, getSettings);

	const getError = (state: IStoreDatagridStateSlice<List, SortableCols>): any => state.error;
	const selectError: MemoizedSelector<object, any> = createSelector(sliceSelector, getError);

	const getIsLoading = (state: IStoreDatagridStateSlice<List, SortableCols>): boolean => state.isLoading;
	const selectIsLoading: MemoizedSelector<object, boolean> = createSelector(sliceSelector, getIsLoading);

	return {
		getList,
		selectList,
		getSettings,
		selectSettings,
		getError,
		selectError,
		getIsLoading,
		selectIsLoading
	};
}
